import { z } from 'zod'

/** Where a web visitor came from to an event page; `app` views are counted apart. */
export const eventViewSources = [
  'share_link',
  'search',
  'instagram',
  'facebook',
  'other_site',
  'pulsify',
  'direct',
] as const

export const eventViewSourceSchema = z.enum(eventViewSources)
export type EventViewSource = z.infer<typeof eventViewSourceSchema>

/** Body of the beacon the website sends once an event page was really looked at. */
export const trackEventWebViewSchema = z.object({ source: eventViewSourceSchema })
export type TrackEventWebView = z.infer<typeof trackEventWebViewSchema>

const SEARCH_HOSTS =
  /(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|ecosia\.org|search\.yahoo\.com|yahoo\.com|startpage\.com|qwant\.com|search\.brave\.com|yandex\.[a-z]+|baidu\.com)$/
const INSTAGRAM_HOSTS = /(^|\.)instagram\.com$/
const FACEBOOK_HOSTS = /(^|\.)(facebook\.com|fb\.com|fb\.me|messenger\.com)$/
const INSTAGRAM_APP = /\bInstagram\b/
const FACEBOOK_APP = /\bFBAN\/|\bFBAV\/|\bFB_IAB\//

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return null
  }
}

/** Sorts one web view of an event page by where the visitor came from. */
export function classifyEventReferrer(input: {
  referrer: string
  ownHost: string
  userAgent?: string
  viaShareLink?: boolean
}): EventViewSource {
  if (input.viaShareLink) return 'share_link'
  const host = hostOf(input.referrer)
  const own = input.ownHost.toLowerCase().replace(/^www\./, '')
  if (host) {
    if (host === own || host.endsWith(`.${own}`)) return 'pulsify'
    if (SEARCH_HOSTS.test(host)) return 'search'
    if (INSTAGRAM_HOSTS.test(host)) return 'instagram'
    if (FACEBOOK_HOSTS.test(host)) return 'facebook'
    return 'other_site'
  }
  const ua = input.userAgent ?? ''
  if (INSTAGRAM_APP.test(ua)) return 'instagram'
  if (FACEBOOK_APP.test(ua)) return 'facebook'
  return 'direct'
}

/** View counts of an upcoming or running event that earn the organizer a short e-mail. */
export const EVENT_VIEW_MILESTONES = [100, 500, 1000, 5000] as const

/** The highest milestone `views` has reached that was not mailed yet, or null. */
export function nextViewMilestone(views: number, sent: readonly number[]): number | null {
  const reached = EVENT_VIEW_MILESTONES.filter((milestone) => milestone <= views)
  const highest = reached[reached.length - 1]
  if (highest === undefined || sent.includes(highest)) return null
  return highest
}

const count = () => z.number().int().nonnegative()

export const eventSummaryViewSourceSchema = z.object({
  source: z.union([eventViewSourceSchema, z.literal('app')]),
  views: count(),
})

/** One hour (ISO start, UTC) of an event's Live Room check-ins. */
export const eventSummaryCheckInHourSchema = z.object({
  hour: z.string(),
  checkIns: count(),
})

/** The numbers an organizer sees about one event once it is over. Counts only, never people. */
export const eventSummaryResponseSchema = z.object({
  eventId: z.string().uuid(),
  title: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  ended: z.boolean(),
  views: z.object({
    total: count(),
    app: count(),
    web: count(),
    bySource: z.array(eventSummaryViewSourceSchema),
    /** Set when the event was online before website views were counted: the first counted day. */
    webCountedSince: z.string().nullable(),
  }),
  answers: z.object({ interested: count(), going: count() }),
  liveRoom: z.object({
    enabled: z.boolean(),
    checkIns: count(),
    peakInside: count(),
    peakAt: z.string().nullable(),
    returningGuests: count(),
    byHour: z.array(eventSummaryCheckInHourSchema),
  }),
  moments: count(),
  shareLinks: z.object({
    visits: count(),
    creditsConfirmed: count(),
    creditsPending: count(),
  }),
})
export type EventSummaryResponse = z.infer<typeof eventSummaryResponseSchema>

/** Whether the signed-in dashboard user gets the e-mails about their events. */
export const eventEmailsSettingSchema = z.object({ enabled: z.boolean() })
export type EventEmailsSetting = z.infer<typeof eventEmailsSettingSchema>

/** Switches the organizer event e-mails off (or back on) from the link in one of them. */
export const eventEmailsOptOutRequestSchema = z.object({
  token: z.string().min(1).max(200),
  subscribed: z.boolean().default(false),
})
export type EventEmailsOptOutRequest = z.input<typeof eventEmailsOptOutRequestSchema>
